import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Switch from "react-switch";
import Swal from "sweetalert2";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { ensureThemeJsonArray, getDvSourceImageUrl } from "../../../functions/themeAssets";
import RulesFeatureTour, {
  markRulesTourSeen,
  shouldAutoStartRulesTour,
} from "./RulesFeatureTour";

const MAX_RULES = 6;
const MIN_RULES = 1;
const MAX_RULE_CHARS = 200;
const MIN_POINTS = 1;
const MIN_QUESTIONS = 1;
const MAX_TITLE_CHARS = 300;
const MAX_THANKYOU_CHARS = 300;

const MIN_CATEGORIES = 3;
const MAX_CATEGORIES = 6;
const MAX_CATEGORY_NAME_CHARS = 15;
const CATEGORY_IMAGE_EXT = ["jpg", "jpeg", "png", "gif"];
const CATEGORY_IMAGE_MAX_MB = 5;
const DEFAULT_CATEGORY_COLORS = [
  "#ff0000",
  "#0000ff",
  "#008000",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
];

function emptyCategoryRow(index = 0) {
  return {
    name: "",
    image: "",
    color: DEFAULT_CATEGORY_COLORS[index % DEFAULT_CATEGORY_COLORS.length],
    file: null,
    preview: null,
  };
}

function categoriesFromThemeData(raw) {
  const list = ensureThemeJsonArray(raw).map((c, i) => ({
    name: String(c?.name ?? "").slice(0, MAX_CATEGORY_NAME_CHARS),
    image: String(c?.image ?? ""),
    color: c?.color
      ? toColorInputValue(c.color)
      : DEFAULT_CATEGORY_COLORS[i % DEFAULT_CATEGORY_COLORS.length],
    file: null,
    preview: c?.image ? getDvSourceImageUrl(c.image) : null,
  }));
  while (list.length < MIN_CATEGORIES) list.push(emptyCategoryRow(list.length));
  return list;
}

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

function isThemeFlagOn(val, defaultOn = false) {
  if (val == null || val === "") return defaultOn;
  return String(val).toLowerCase() === "true";
}

function toColorInputValue(val) {
  if (!val || typeof val !== "string") return "#000000";
  const v = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_COLORS[v.toLowerCase()];
  if (named) return named;
  return "#000000";
}

const normalizeCountInput = (raw) => String(raw).replace(/\D/g, "");

function parseRulesList(raw) {
  if (Array.isArray(raw)) return raw.map((r) => String(r ?? ""));
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((r) => String(r ?? ""));
    } catch {
      /* not JSON */
    }
  }
  return [];
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([""]);
  const [titleText, setTitleText] = useState("");
  const [thankYouText, setThankYouText] = useState("");
  const [correctPoints, setCorrectPoints] = useState(String(MIN_POINTS));
  const [noOfQuestion, setNoOfQuestion] = useState(String(MIN_QUESTIONS));
  const [shuffle, setShuffle] = useState(false);
  const [boxTextColor, setBoxTextColor] = useState("#000000");
  const [titleRulesColor, setTitleRulesColor] = useState("#000000");
  const [buttonColor, setButtonColor] = useState("#e9695e");
  const [tourOpen, setTourOpen] = useState(false);
  const [categories, setCategories] = useState(() => categoriesFromThemeData([]));
  const [categoriesSaving, setCategoriesSaving] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (status === "loading" && !data) return undefined;
    if (!shouldAutoStartRulesTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 600);
    return () => window.clearTimeout(t);
  }, [status, data]);

  const closeTour = () => {
    markRulesTourSeen();
    setTourOpen(false);
  };

  useEffect(() => {
    if (!data) return;
    const loadedRules = parseRulesList(data.rules);
    setRules(loadedRules.length ? loadedRules : [""]);
    setTitleText(String(data.landing_page_title ?? "").slice(0, MAX_TITLE_CHARS));
    setThankYouText(String(data.custom_text_thank_you_page ?? "").slice(0, MAX_THANKYOU_CHARS));
    setCorrectPoints(String(parseInt(data.correctPoints ?? data.points, 10) || MIN_POINTS));
    setNoOfQuestion(String(parseInt(data.noOfQuestion, 10) || MIN_QUESTIONS));
    setShuffle(isThemeFlagOn(data.shuffle, false));
    setBoxTextColor(toColorInputValue(data.landing_page_title_color));
    setTitleRulesColor(toColorInputValue(data.title_color));
    setButtonColor(toColorInputValue(data.button_color || "#e9695e"));
    setCategories(categoriesFromThemeData(data.categories));
  }, [data]);

  const updateRule = (index, value) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? value : rule)));
  };

  const addRule = () => {
    if (rules.length >= MAX_RULES) {
      Swal.fire("Error", `Maximum ${MAX_RULES} rules allowed`, "error");
      return;
    }
    setRules((prev) => [...prev, ""]);
  };

  const removeRule = (index) => {
    if (rules.length <= MIN_RULES) {
      Swal.fire("Error", "At least 1 rule is required", "error");
      return;
    }
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCategoryName = (index, value) => {
    const name = value.slice(0, MAX_CATEGORY_NAME_CHARS);
    setCategories((prev) => prev.map((c, i) => (i === index ? { ...c, name } : c)));
  };

  const updateCategoryImage = async (index, file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!CATEGORY_IMAGE_EXT.includes(ext)) {
      Swal.fire("Error", `Only ${CATEGORY_IMAGE_EXT.join(", ")} formats allowed`, "error");
      return;
    }
    if (file.size > CATEGORY_IMAGE_MAX_MB * 1024 * 1024) {
      Swal.fire("Error", `File size exceeds ${CATEGORY_IMAGE_MAX_MB}MB limit`, "error");
      return;
    }
    const preview = URL.createObjectURL(file);
    setCategories((prev) => prev.map((c, i) => (i === index ? { ...c, file, preview } : c)));
  };

  const updateCategoryColor = (index, value) => {
    setCategories((prev) => prev.map((c, i) => (i === index ? { ...c, color: value } : c)));
  };

  const addCategory = () => {
    if (categories.length >= MAX_CATEGORIES) {
      Swal.fire("Error", `Maximum ${MAX_CATEGORIES} categories allowed`, "error");
      return;
    }
    setCategories((prev) => [...prev, emptyCategoryRow(prev.length)]);
  };

  const removeCategory = (index) => {
    if (categories.length <= MIN_CATEGORIES) {
      Swal.fire("Error", `At least ${MIN_CATEGORIES} categories are required`, "error");
      return;
    }
    setCategories((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveCategories = async () => {
    const trimmed = categories.map((c) => ({ ...c, name: c.name.trim() }));

    if (trimmed.length < MIN_CATEGORIES || trimmed.length > MAX_CATEGORIES) {
      Swal.fire("Error", `You must have between ${MIN_CATEGORIES} and ${MAX_CATEGORIES} categories.`, "error");
      return;
    }
    if (trimmed.some((c) => !c.name)) {
      Swal.fire("Error", "Every category needs a name.", "error");
      return;
    }
    const namesLower = trimmed.map((c) => c.name.toLowerCase());
    if (new Set(namesLower).size !== namesLower.length) {
      Swal.fire("Error", "Category names must be unique.", "error");
      return;
    }
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Admin session or theme is missing.", "error");
      return;
    }

    setCategoriesSaving(true);
    try {
      const formData = new FormData();
      formData.append("themeName", currentTheme);
      formData.append("organizationId", organizationId || "admin");
      formData.append("sessionId", sessionId || "admin");

      const payloadCategories = trimmed.map((c, i) => {
        const color = toColorInputValue(c.color);
        if (c.file) {
          const token = `catimg_${i}`;
          formData.append(token, c.file);
          return { name: c.name, image: `__UPLOAD__:${token}`, color };
        }
        return { name: c.name, image: c.image || "", color };
      });
      formData.append("extraData", JSON.stringify({ categories: payloadCategories }));

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateThemeMedia`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to save categories");
      }

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success", "Categories saved", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save categories", "error");
    } finally {
      setCategoriesSaving(false);
    }
  };

  const validate = () => {
    const trimmed = rules.map((r) => String(r ?? "").trim());
    const filled = trimmed.filter(Boolean);
    const errors = [];

    if (!filled.length) {
      errors.push("At least 1 rule is required");
    }
    const longRules = trimmed
      .map((text, i) => (text.length > MAX_RULE_CHARS ? i + 1 : null))
      .filter(Boolean);
    if (longRules.length) {
      errors.push(`Rules ${longRules.join(", ")} exceed ${MAX_RULE_CHARS} characters`);
    }
    if (rules.length > MAX_RULES) {
      errors.push(`Maximum ${MAX_RULES} rules allowed`);
    }
    if (titleText.trim().length > MAX_TITLE_CHARS) {
      errors.push(`Custom title text exceeds ${MAX_TITLE_CHARS} characters`);
    }
    if (thankYouText.trim().length > MAX_THANKYOU_CHARS) {
      errors.push(`Thank you page text exceeds ${MAX_THANKYOU_CHARS} characters`);
    }
    if (parseInt(correctPoints, 10) < MIN_POINTS || correctPoints === "") {
      errors.push("You must set at least 1 correct answer point.");
    }
    if (parseInt(noOfQuestion, 10) < MIN_QUESTIONS || noOfQuestion === "") {
      errors.push("You must upload at least 1 question.");
    }

    if (errors.length) return { ok: false, msg: errors.join("\n") };

    const payload = {
      landing_page_title: titleText.trim(),
      landing_page_title_color: boxTextColor,
      title_color: titleRulesColor,
      button_color: buttonColor,
      points: parseInt(correctPoints, 10),
      noOfQuestion: parseInt(noOfQuestion, 10),
      shuffle: shuffle ? "true" : "false",
      custom_text_thank_you_page: thankYouText.trim(),
      rules: filled,
    };

    return { ok: true, payload, filled };
  };

  const handleSave = async ({ goNext = false } = {}) => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Admin session or theme is missing.", "error");
      return;
    }

    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success", "Data Updated", "success");
      if (goNext) navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  const switchProps = useMemo(
    () => ({
      onColor: buttonColor || "#e25569",
      offColor: "#cbd5e1",
      onHandleColor: "#ffffff",
      offHandleColor: "#ffffff",
      height: 22,
      width: 48,
      handleDiameter: 18,
      uncheckedIcon: false,
      checkedIcon: false,
    }),
    [buttonColor]
  );

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="rules-shell">
      <div className="back-button-holder rules-top-bar admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <div className="rules-page-header">
        <h1 className="rules-page-header-title">Rules &amp; Game Settings</h1>
        <p className="rules-page-header-desc">
          Configure what players read, how the game scores and shuffles, and the colors used
          throughout this theme.
        </p>
      </div>

      <div className="rules-main-grid">
        <section className="rules-card" data-tour="tour-theme-settings">
          <header className="rules-card-header">
            <span className="rules-card-icon">
              <i className="fa-solid fa-align-left" aria-hidden />
            </span>
            <div>
              <h2 className="rules-card-title">Rules &amp; Messaging</h2>
              <p className="rules-card-subtitle">What players read before, and after, the game</p>
            </div>
          </header>

          <div className="rules-card-body">
            <div className="rules-field-group">
              <label className="rules-field-label" htmlFor="rules-title-text">
                Custom Title Text
              </label>
              <textarea
                id="rules-title-text"
                className="rules-textarea"
                maxLength={MAX_TITLE_CHARS}
                value={titleText}
                onChange={(e) => setTitleText(e.target.value)}
              />
              <p className="rules-field-hint">
                <code>Shown on the main page.</code>
                <code>Max {MAX_TITLE_CHARS} characters.</code>
              </p>
            </div>

            <div className="rules-field-group" data-tour="tour-custom-rules">
              <label className="rules-field-label">Game Rules</label>
              <div className="rules-repeater">
                {rules.map((rule, index) => {
                  const charCount = String(rule ?? "").length;
                  const overLimit = charCount > MAX_RULE_CHARS;
                  return (
                    <div className="rules-repeater-row" key={`rule-${index}`}>
                      <span className="rules-repeater-index">{index + 1}</span>
                      <div className="rules-repeater-field">
                        <textarea
                          name="rules"
                          className="rules-repeater-textarea"
                          placeholder="Rules"
                          value={rule}
                          onChange={(e) => updateRule(index, e.target.value)}
                        />
                        <p className={`rules-char-count${overLimit ? " is-over" : ""}`}>
                          {charCount}/{MAX_RULE_CHARS}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="rules-remove-btn"
                        onClick={() => removeRule(index)}
                        disabled={rules.length <= MIN_RULES || loading}
                        aria-label="Remove rule"
                        title="Remove rule"
                      >
                        <i className="fa-solid fa-xmark" aria-hidden />
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="rules-add-btn"
                onClick={addRule}
                disabled={rules.length >= MAX_RULES || loading}
              >
                <i className="fa-solid fa-plus" aria-hidden /> Add new rule
              </button>
              <p className="rules-field-hint">
                <code>Up to {MAX_RULES} rules</code>
                <code>At least {MIN_RULES} required</code>
                <code>Max {MAX_RULE_CHARS} characters each</code>
              </p>
            </div>

            <div className="rules-field-group">
              <label className="rules-field-label" htmlFor="rules-thankyou-text">
                Thank You Page Text
              </label>
              <input
                id="rules-thankyou-text"
                type="text"
                className="rules-input"
                maxLength={MAX_THANKYOU_CHARS}
                value={thankYouText}
                onChange={(e) => setThankYouText(e.target.value)}
              />
              <p className="rules-field-hint">
                <code>Shown on the thank you page.</code>
                <code>Max {MAX_THANKYOU_CHARS} characters.</code>
              </p>
            </div>
          </div>
        </section>

        <div className="rules-stack" data-tour="tour-page-content">
          <section className="rules-card">
            <header className="rules-card-header">
              <span className="rules-card-icon">
                <i className="fa-solid fa-sliders" aria-hidden />
              </span>
              <div>
                <h2 className="rules-card-title">Gameplay</h2>
                <p className="rules-card-subtitle">Scoring and question behavior</p>
              </div>
            </header>

            <div className="rules-card-body">
              <div className="rules-setting-row">
                <label htmlFor="rules-correct-points" className="rules-setting-label">
                  Correct Answer Points
                </label>
                <input
                  id="rules-correct-points"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="rules-setting-input"
                  value={correctPoints}
                  onChange={(e) => setCorrectPoints(normalizeCountInput(e.target.value))}
                  onBlur={() => {
                    const n = parseInt(correctPoints, 10);
                    if (correctPoints === "" || Number.isNaN(n) || n < MIN_POINTS) {
                      setCorrectPoints(String(MIN_POINTS));
                    }
                  }}
                />
              </div>

              <div className="rules-setting-row">
                <label htmlFor="rules-no-of-question" className="rules-setting-label">
                  No of questions to display
                </label>
                <input
                  id="rules-no-of-question"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="rules-setting-input"
                  value={noOfQuestion}
                  onChange={(e) => setNoOfQuestion(normalizeCountInput(e.target.value))}
                  onBlur={() => {
                    const n = parseInt(noOfQuestion, 10);
                    if (noOfQuestion === "" || Number.isNaN(n) || n < MIN_QUESTIONS) {
                      setNoOfQuestion(String(MIN_QUESTIONS));
                    }
                  }}
                />
              </div>

              <div className="rules-setting-row">
                <label htmlFor="rules-shuffle" className="rules-setting-label">
                  Shuffle Questions
                </label>
                <Switch
                  id="rules-shuffle"
                  checked={shuffle}
                  onChange={setShuffle}
                  disabled={loading}
                  {...switchProps}
                />
              </div>
            </div>
          </section>

          <section className="rules-card">
            <header className="rules-card-header">
              <span className="rules-card-icon">
                <i className="fa-solid fa-palette" aria-hidden />
              </span>
              <div>
                <h2 className="rules-card-title">Appearance</h2>
                <p className="rules-card-subtitle">Colors used across rules &amp; game screens</p>
              </div>
            </header>

            <div className="rules-card-body">
              <div className="rules-color-grid">
                <label className="rules-color-chip">
                  <span
                    className="rules-color-chip-swatch"
                    style={{ background: boxTextColor }}
                  >
                    <input
                      type="color"
                      value={boxTextColor}
                      onChange={(e) => setBoxTextColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Box Text</span>
                </label>

                <label className="rules-color-chip">
                  <span
                    className="rules-color-chip-swatch"
                    style={{ background: titleRulesColor }}
                  >
                    <input
                      type="color"
                      value={titleRulesColor}
                      onChange={(e) => setTitleRulesColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Title &amp; Rules</span>
                </label>

                <label className="rules-color-chip">
                  <span
                    className="rules-color-chip-swatch"
                    style={{ background: buttonColor }}
                  >
                    <input
                      type="color"
                      value={buttonColor}
                      onChange={(e) => setButtonColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Button</span>
                </label>
              </div>
            </div>
          </section>

          <section className="rules-card rules-categories-card" data-tour="tour-categories">
            <header className="rules-card-header">
              <span className="rules-card-icon">
                <i className="fa-solid fa-layer-group" aria-hidden />
              </span>
              <div>
                <h2 className="rules-card-title">Answer Categories</h2>
                <p className="rules-card-subtitle">
                  e.g. Diversity, Equity, Inclusion — shared by every question. Each category's
                  color and image are used for its bucket in the game.
                </p>
              </div>
            </header>

            <div className="rules-card-body">
              <div className="rules-categories-list">
                <div className="rules-category-header" aria-hidden="true">
                  <span>Image</span>
                  <span>Category name</span>
                  <span>Color</span>
                  <span />
                </div>

                {categories.map((cat, index) => (
                  <div className="rules-category-row" key={`category-${index}`}>
                    <button
                      type="button"
                      className="rules-category-image-btn"
                      onClick={() =>
                        document.getElementById(`rules-category-file-${index}`)?.click()
                      }
                      disabled={categoriesSaving}
                      aria-label="Upload category image"
                    >
                      {cat.preview ? (
                        <img src={cat.preview} alt="" className="rules-category-image-preview" />
                      ) : (
                        <i className="fa-solid fa-image" aria-hidden />
                      )}
                    </button>
                    <input
                      id={`rules-category-file-${index}`}
                      type="file"
                      accept="image/jpeg,image/png,image/gif"
                      className="rules-begin-file-input"
                      onChange={(e) => updateCategoryImage(index, e.target.files?.[0])}
                      disabled={categoriesSaving}
                    />

                    <div className="rules-category-name-field">
                      <input
                        type="text"
                        className="rules-category-name-input"
                        placeholder="Category name"
                        value={cat.name}
                        maxLength={MAX_CATEGORY_NAME_CHARS}
                        onChange={(e) => updateCategoryName(index, e.target.value)}
                        disabled={categoriesSaving}
                      />
                      <span className="rules-category-char-count">
                        {cat.name.length}/{MAX_CATEGORY_NAME_CHARS}
                      </span>
                    </div>

                    <input
                      type="color"
                      className="rules-category-color-input"
                      value={toColorInputValue(cat.color)}
                      onChange={(e) => updateCategoryColor(index, e.target.value)}
                      disabled={categoriesSaving}
                      aria-label="Category color"
                    />

                    <button
                      type="button"
                      className="rules-remove-btn"
                      onClick={() => removeCategory(index)}
                      disabled={categories.length <= MIN_CATEGORIES || categoriesSaving}
                      aria-label="Remove category"
                      title="Remove category"
                    >
                      <i className="fa-solid fa-xmark" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="rules-add-btn"
                onClick={addCategory}
                disabled={categories.length >= MAX_CATEGORIES || categoriesSaving}
              >
                <i className="fa-solid fa-plus" aria-hidden /> Add new category
              </button>

              <p className="rules-field-hint">
                <code>Up to {MAX_CATEGORIES}</code>
                <code>Max {MAX_CATEGORY_NAME_CHARS} chars</code>
                <code>Min {MIN_CATEGORIES} required</code>
              </p>

              <div className="rules-categories-save-holder">
                <button
                  type="button"
                  className="rules-categories-save-btn"
                  onClick={handleSaveCategories}
                  disabled={categoriesSaving}
                >
                  {categoriesSaving ? "Saving..." : "Save Categories"}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="rules-sticky-actions" data-tour="tour-actions">
        <button
          type="button"
          className="save-and-continue"
          onClick={() => handleSave({ goNext: true })}
          disabled={loading}
        >
          {loading ? "Saving..." : "Next"} <i className="fa-solid fa-angle-double-right" aria-hidden />
        </button>
      </div>

      <RulesFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default Rules;
